import React from 'react';
import { Toaster } from 'react-hot-toast';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from 'react-router-dom';

import Archived from '../pages/archived';
import AddRole from '../../components/pages/roles/addRole';
import ChangePassword from '../../components/pages/auth/changePassword';
import Connectivity from '../../components/themed/connectivity';
import Dashboard from '../../components/pages/dashboard';
import Details from '../../components/pages/projects/details';
import Division from '../pages/divisions';
import ForgotPassword from '../../components/pages/auth/forgotPassword';
import InternalUserDashboard from '../../components/pages/dashboard/internalUserDashboard';
import ExternalUserDashboard from '../../components/pages/dashboard/externalUserDashboard';
import FallBack from '../themed/fallback';
import GeneralReportOverview from '../pages/general-report-overview';
import Login from '../../components/pages/auth/login';
import Logout from '../../components/common/logout';
import NewReport from '../pages/reports';
import Organizations from '../../components/pages/users/organizations';
import OrganizationDashboard from '../../components/pages/projects/organizationDashboard';
import OrganizationDetails from '../../components/pages/users/organizationDetails';
import ProgressReport from '../pages/progress-report';
import Projects from '../../components/pages/projects';
import ProtectedRoute from '../../components/common/protectedRoute';
import PublicRoute from '../../components/common/publicRoute';
import ProfileSettings from '../pages/users/profileSettings';
import ReportUpload from '../pages/report-upload';
import RoleBasedRoute from '../../components/common/roleBasedRoute';
import Tutorials from '../pages/tutorials';
import UserBasedRoute from '../../components/common/userBasedRoute';
import Unauthorized from '../../components/pages/unauthorized';
import Users from '../../components/pages/users';
import UserDetails from '../../components/pages/users/userDetails';

import useConnectivity from '../../hooks/useConnectivity';
import useTutorial from '../../hooks/useTutorial';

import routes from '../../utils/routes';
import { USER_TYPES } from '../../utils/constants';

const Wrapper: React.FC = (): JSX.Element => {
	useConnectivity();
	useTutorial();

	return (
		<React.Fragment>
			<Toaster containerStyle={{ zIndex: 999999999 }} />

			<Connectivity />

			<React.Suspense fallback={<FallBack />}>
				<Router>
					<Routes>
						<Route element={<ProtectedRoute />}>
							<Route path={routes.logout.pathname} element={<Logout />} />

							<Route path={routes.dashboard.pathname} element={<Dashboard />}>
								<Route path={routes.unauthorized.pathname} element={<Unauthorized />} />

								<Route element={<UserBasedRoute userType={USER_TYPES.EXTERNAL} redirectPath={routes.internalUserDashboard.pathname} />}>
									<Route index element={<Navigate to={routes.externalUserDashboard.pathname} />} />
									<Route path={routes.externalUserDashboard.pathname} element={<ExternalUserDashboard />} />
								</Route>

								<Route element={<UserBasedRoute userType={USER_TYPES.INTERNAL} redirectPath={routes.externalUserDashboard.pathname} />}>
									<Route index element={<Navigate to={routes.internalUserDashboard.pathname} />}/>
									<Route path={routes.internalUserDashboard.pathname} element={<InternalUserDashboard />}/>
								</Route>

								<Route
									element={
										<UserBasedRoute
											userType={USER_TYPES.INTERNAL}
											redirectPath={routes.unauthorized.pathname}
										/>
									}
								>
									<Route
										path={routes.projects.pathname}
										element={<Projects />}
									/>
								</Route>

								<Route
									element={
										<RoleBasedRoute
											allowedRoles={['manageReports', 'approveReports']}
											redirectPath={routes.unauthorized.pathname}
										/>
									}
								>
								</Route>

								<Route
									element={
										<UserBasedRoute
											userType={USER_TYPES.EXTERNAL}
											redirectPath={routes.externalUserDashboard.pathname}
										/>
									}
								>
									<Route
										path={routes.organizationDashboard.pathname}
										element={<OrganizationDashboard />}
									/>
								</Route>

								<Route
									element={
										<RoleBasedRoute
											allowedRoles={[
												'manageProjects',
												'manageReports',
												'viewReports',
												'viewAssociatedReports',
												'reviewReports',
												'approveReports',
											]}
											redirectPath={routes.internalUserDashboard.pathname}
										/>
									}
								>
									<Route
										element={
											<RoleBasedRoute
												allowedRoles={[
													'reviewReports',
													'approveReports',
													'manageReports',
													'viewReports',
													'viewAssociatedReports'
												]}
												redirectPath={routes.projects.pathname}
											/>
										}
									>
										<Route
											path={routes.projectProgress.pathname}
											element={<ProgressReport />}
										/>
									</Route>

									<Route
										element={
											<RoleBasedRoute
												allowedRoles={['manageReports', 'approveReports']}
												redirectPath={routes.unauthorized.pathname}
											/>
										}
									>
										<Route
											path={routes.newReport.pathname}
											element={<NewReport />}
										/>
									</Route>

									<Route
										path={routes.projectDetails.pathname}
										element={<Details />}
									/>
								</Route>
								
								<Route element={<RoleBasedRoute allowedRoles={['approveReports']} redirectPath={routes.unauthorized.pathname} />}>
									<Route path={routes.archivedProjects.pathname} element={<Archived />}/>
								</Route>
								
								<Route
									element={
										<RoleBasedRoute
											allowedRoles={['approveReports', 'viewReports', 'viewAssociatedReports']}
											redirectPath={routes.unauthorized.pathname}
										/>
									}
								>
									<Route path={routes.inspectionTracker.pathname} element={<GeneralReportOverview />} />
								</Route>

								<Route element={<RoleBasedRoute allowedRoles={['manageUsers']} redirectPath={routes.unauthorized.pathname} />}>
									<Route path={routes.users.pathname} element={<Users />} />
									<Route path={routes.userDetails.pathname} element={<UserDetails />} />
								</Route>

								<Route element={<RoleBasedRoute allowedRoles={['manageRoles']} redirectPath={routes.unauthorized.pathname} />}>
									<Route path={routes.organizations.pathname} element={<Organizations />}/>
									<Route path={routes.divisions.pathname} element={<Division />}/>
									<Route path={routes.organizationDetails.pathname} element={<OrganizationDetails />}/>
									<Route  path={routes.addRole.pathname}  element={<AddRole />} />
								</Route>

								<Route element={<RoleBasedRoute allowedRoles={['approveReports']} redirectPath={routes.unauthorized.pathname} />}>
									<Route path={routes.reportUpload.pathname} element={<ReportUpload />}/>
								</Route>

								<Route path={routes.profileSettings.pathname} element={<ProfileSettings />} />
								<Route path={routes.tutorials.pathname} element={<Tutorials />} />
							</Route>
						</Route>

						<Route element={<PublicRoute />}>
							<Route path={routes.login.pathname} element={<Login />} />
							<Route path={routes.changePassword.pathname} element={<ChangePassword />} />
							<Route path={routes.forgotPassword.pathname} element={<ForgotPassword />} />
						</Route>

						<Route path={routes.wildcard.pathname} element={<Navigate to={routes.dashboard.pathname} />} />
					</Routes>
				</Router>
			</React.Suspense>
		</React.Fragment>
	);
};

export default React.memo(Wrapper);
